import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import {
    FunnelStage,
    FUNNEL_STAGES,
    FUNNEL_STAGE_MAP,
    SalesFunnelProspect,
    StageDetailsPayload,
} from '../../salesFunnel.types';
import { advanceSalesFunnelProspect, updateSalesFunnelStageDetails } from '../../salesFunnel.api';

interface StageDetailsFormProps {
    open: boolean;
    onClose: () => void;
    stage: FunnelStage;
    prospect: SalesFunnelProspect;
    orgUuid: string;
    onSaved: () => void;
    advanceOnSave?: boolean;
}

const StageDetailsForm: React.FC<StageDetailsFormProps> = ({
    open,
    onClose,
    stage,
    prospect,
    orgUuid,
    onSaved,
    advanceOnSave = false,
}) => {
    const stageConfig = FUNNEL_STAGE_MAP[stage];
    const stageNote = prospect.stage_details?.find((sd) => sd.stage === stage);

    const getInitialValues = (): StageDetailsPayload => {
        const base: StageDetailsPayload = {
            stage,
            notes: stageNote?.notes || '',
        };

        switch (stage) {
            case 'discovery':
                return {
                    ...base,
                    use_case: prospect.discovery_details?.use_case || '',
                    budget_range: prospect.discovery_details?.budget_range || '',
                    athlete_count_confirmed:
                        prospect.discovery_details?.athlete_count_confirmed ?? undefined,
                    facility_details: prospect.discovery_details?.facility_details || '',
                    meeting_date: prospect.discovery_details?.meeting_date || '',
                    meeting_notes: prospect.discovery_details?.meeting_notes || '',
                };
            case 'sized':
                return {
                    ...base,
                    indoor_units: prospect.sizing_details?.indoor_units ?? 0,
                    outdoor_units: prospect.sizing_details?.outdoor_units ?? 0,
                    beacons: prospect.sizing_details?.beacons ?? 0,
                    install_scope: prospect.sizing_details?.install_scope || '',
                    estimated_value: prospect.sizing_details?.estimated_value ?? undefined,
                };
            case 'proposal':
                return {
                    ...base,
                    proposal_sent_at: prospect.proposal_details?.proposal_sent_at
                        ? prospect.proposal_details.proposal_sent_at.split('T')[0]
                        : '',
                    proposal_amount: prospect.proposal_details?.proposal_amount ?? undefined,
                    proposal_notes: prospect.proposal_details?.proposal_notes || '',
                };
            case 'verbal':
            case 'closed':
                return {
                    ...base,
                    loi_signed_at: prospect.close_details?.loi_signed_at
                        ? prospect.close_details.loi_signed_at.split('T')[0]
                        : '',
                    contract_signed_at: prospect.close_details?.contract_signed_at
                        ? prospect.close_details.contract_signed_at.split('T')[0]
                        : '',
                    contract_value: prospect.close_details?.contract_value ?? undefined,
                };
            case 'launch_install':
                return {
                    ...base,
                    install_start_date: prospect.install_details?.install_start_date || '',
                    install_end_date: prospect.install_details?.install_end_date || '',
                    indoor_units_installed: prospect.install_details?.indoor_units_installed ?? 0,
                    outdoor_units_installed: prospect.install_details?.outdoor_units_installed ?? 0,
                    beacons_installed: prospect.install_details?.beacons_installed ?? 0,
                    install_status: prospect.install_details?.install_status || 'pending',
                    install_notes: prospect.install_details?.install_notes || '',
                };
            default:
                return base;
        }
    };

    const formik = useFormik({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        onSubmit: async (values, { setSubmitting }) => {
            try {
                await updateSalesFunnelStageDetails(orgUuid, prospect.uuid, values);
                if (advanceOnSave) {
                    await advanceSalesFunnelProspect(orgUuid, prospect.uuid);
                }
                onSaved();
            } catch (err) {
                console.error('Failed to save stage details', err);
            } finally {
                setSubmitting(false);
            }
        },
    });

    const renderStageFields = () => {
        switch (stage) {
            case 'discovery':
                return (
                    <>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Use Case"
                                name="use_case"
                                value={formik.values.use_case || ''}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Budget Range"
                                name="budget_range"
                                value={formik.values.budget_range || ''}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Confirmed Athlete Count"
                                name="athlete_count_confirmed"
                                value={formik.values.athlete_count_confirmed ?? ''}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Facility Details"
                                name="facility_details"
                                value={formik.values.facility_details || ''}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Meeting Date"
                                name="meeting_date"
                                value={formik.values.meeting_date || ''}
                                onChange={formik.handleChange}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Meeting Notes"
                                name="meeting_notes"
                                value={formik.values.meeting_notes || ''}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                    </>
                );
            case 'sized':
                return (
                    <>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Indoor Units"
                                name="indoor_units"
                                value={formik.values.indoor_units ?? 0}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Outdoor Units"
                                name="outdoor_units"
                                value={formik.values.outdoor_units ?? 0}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Beacons"
                                name="beacons"
                                value={formik.values.beacons ?? 0}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Install Scope"
                                name="install_scope"
                                value={formik.values.install_scope || ''}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Estimated Value ($)"
                                name="estimated_value"
                                value={formik.values.estimated_value ?? ''}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Grid>
                    </>
                );
            case 'proposal':
                return (
                    <>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Proposal Sent Date"
                                name="proposal_sent_at"
                                value={formik.values.proposal_sent_at || ''}
                                onChange={formik.handleChange}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Proposal Amount ($)"
                                name="proposal_amount"
                                value={formik.values.proposal_amount ?? ''}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Proposal Notes"
                                name="proposal_notes"
                                value={formik.values.proposal_notes || ''}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                    </>
                );
            case 'verbal':
            case 'closed':
                return (
                    <>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="LOI Signed Date"
                                name="loi_signed_at"
                                value={formik.values.loi_signed_at || ''}
                                onChange={formik.handleChange}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Contract Signed Date"
                                name="contract_signed_at"
                                value={formik.values.contract_signed_at || ''}
                                onChange={formik.handleChange}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Contract Value ($)"
                                name="contract_value"
                                value={formik.values.contract_value ?? ''}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0, step: 0.01 }}
                            />
                        </Grid>
                    </>
                );
            case 'launch_install':
                return (
                    <>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Install Start Date"
                                name="install_start_date"
                                value={formik.values.install_start_date || ''}
                                onChange={formik.handleChange}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                type="date"
                                label="Install End Date"
                                name="install_end_date"
                                value={formik.values.install_end_date || ''}
                                onChange={formik.handleChange}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Indoor Installed"
                                name="indoor_units_installed"
                                value={formik.values.indoor_units_installed ?? 0}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Outdoor Installed"
                                name="outdoor_units_installed"
                                value={formik.values.outdoor_units_installed ?? 0}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                type="number"
                                label="Beacons Installed"
                                name="beacons_installed"
                                value={formik.values.beacons_installed ?? 0}
                                onChange={formik.handleChange}
                                size="small"
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                select
                                label="Install Status"
                                name="install_status"
                                value={formik.values.install_status || 'pending'}
                                onChange={formik.handleChange}
                                size="small"
                            >
                                <MenuItem value="pending">Pending</MenuItem>
                                <MenuItem value="in_progress">In Progress</MenuItem>
                                <MenuItem value="completed">Completed</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                label="Install Notes"
                                name="install_notes"
                                value={formik.values.install_notes || ''}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                    </>
                );
            default:
                return null;
        }
    };

    const nextStageConfig = advanceOnSave
        ? FUNNEL_STAGES.find((s) => s.order === (stageConfig?.order ?? 0) + 1) ?? null
        : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <form onSubmit={formik.handleSubmit}>
                <DialogTitle>
                    {advanceOnSave && nextStageConfig
                        ? `Complete ${stageConfig?.label} → Advance to ${nextStageConfig.label}`
                        : `Edit ${stageConfig?.label || stage} Details`}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        {renderStageFields()}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Notes"
                                name="notes"
                                value={formik.values.notes || ''}
                                onChange={formik.handleChange}
                                size="small"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
                        {advanceOnSave && nextStageConfig
                            ? `Save & Advance to ${nextStageConfig.label}`
                            : 'Save'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default StageDetailsForm;
